import styles from "../../../../components/SelectTrigger/internal/SelectTriggerBase.module.scss";
import cx from "classnames";
import { Size } from "../../../../types";
import { Button } from "../../../../components/Button";
import Chevron_Right from "@servicetitan/hammer-icon/mdi/round/chevron_right.svg";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import { ReactElement, RefObject } from "react";
import { useMergeRefs } from "../../../../hooks/useMergeRefs/useMergeRefs";

export type SelectFieldTriggerProps = {
  placeholder?: string;
  size?: Extract<Size, "small" | "medium" | "large">;
  disableClearButton?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  onClear?: () => void;
  id?: string;
  popoverTarget?: string;
  inputWrapperRef?: RefObject<HTMLDivElement>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toggleButtonProps?: Record<string, any>;
  /** The display text (selected option label or empty) */
  displayValue?: string;
  prefix?: string | ReactElement;
  suffix?: string | ReactElement;
};

/**
 * Non-searchable select trigger for SelectField when disableSearch is true.
 * Renders a div-based trigger instead of an input, suitable for the listbox ARIA pattern.
 */
export const SelectFieldTrigger = ({
  placeholder,
  size,
  disableClearButton,
  disabled,
  readOnly,
  error,
  onClear,
  id,
  inputWrapperRef,
  toggleButtonProps,
  displayValue,
  prefix,
  suffix,
  popoverTarget,
}: SelectFieldTriggerProps) => {
  const { ref: downshiftRef, ...restToggleButtonProps } =
    toggleButtonProps ?? {};
  const mergedRef = useMergeRefs([inputWrapperRef, downshiftRef]);

  const inputWrapperClassNames = cx(styles["input-wrapper"], {
    [styles["small"]]: size === "small",
    [styles["large"]]: size === "large",
    [styles["no-clear-button"]]: disableClearButton,
    [styles["disabled"]]: disabled || readOnly,
  });

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={mergedRef}
        {...restToggleButtonProps}
        // @ts-expect-error: popovertarget is a valid HTML attribute
        popovertarget={popoverTarget}
        id={id}
        className={cx(inputWrapperClassNames, restToggleButtonProps?.className)}
      >
        {prefix ? <div className={styles["prefix"]}>{prefix}</div> : null}
        <div
          className={cx(styles["input"], {
            [styles["error"]]: error,
          })}
        >
          {displayValue ? (
            <span>{displayValue}</span>
          ) : (
            <span className={styles["fake-placeholder"]}>{placeholder}</span>
          )}
        </div>
        {suffix ? <div className={styles["suffix"]}>{suffix}</div> : null}
        <div className={styles["buttons-wrapper"]}>
          <div className={styles["toggle-button-wrapper"]}>
            <Button
              icon={Chevron_Right}
              aria-label="toggle menu"
              appearance="ghost"
              size="small"
              disabled={disabled}
              tabIndex={-1}
              className={styles["toggle-button"]}
            />
          </div>
        </div>
      </div>
      {disableClearButton ? null : (
        <div
          className={styles["buttons-wrapper"]}
          style={{ insetInlineEnd: "2rem" }}
        >
          <div className={styles["close-button-wrapper"]}>
            <Button
              icon={Close}
              aria-label="clear selection"
              appearance="ghost"
              size="small"
              className={styles["close-button"]}
              onClick={(e) => {
                e.stopPropagation();
                onClear?.();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

SelectFieldTrigger.displayName = "SelectFieldTrigger";
